// ─── src/CatalystWaitlistView.tsx ─────────────────────────────────────────────
import { useState, useCallback } from "react";
import { Download, Mail, Search, X, Trash2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCatalystWaitlist,
  useCatalystWaitlistStats,
  useDeleteCatalystWaitlistEntry,
  catalystWaitlistKeys,
} from "./hooks/useCatalystWaitlist";
import { Pagination }    from "./components/dashboard/Pagination";
import { TableSkeleton } from "./components/ui/Skeleton";
import { StatCard }      from "./components/ui/StatCard";
import type { CatalystWaitlistEntry } from "./types/catalystWaitlist";

const LIMIT = 20;

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function exportCsv(rows: CatalystWaitlistEntry[]) {
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = ["Email,Source,Joined At", ...rows.map((r) =>
    [r.email, r.source ?? "", r.createdAt].map(esc).join(","),
  )].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `catalyst_waitlist_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function CatalystWaitlistView() {
  const qc = useQueryClient();
  const [search, setSearch]         = useState("");
  const [visualPage, setVisualPage] = useState(1);

  const { data: stats } = useCatalystWaitlistStats();
  const { data: allData } = useCatalystWaitlist({ page: 1, limit: 2000 });
  const allRows = allData?.data ?? [];

  const { data: tableData, isLoading, isFetching, isError } = useCatalystWaitlist({
    page: visualPage,
    limit: LIMIT,
    ...(search ? { search } : {}),
  });

  const { mutate: deleteEntry, isPending: isDeleting } = useDeleteCatalystWaitlistEntry();

  const pageRows  = tableData?.data ?? [];
  const tableMeta = tableData?.meta ?? { total: 0, page: 1, limit: LIMIT, totalPages: 1 };

  const handleRefresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: catalystWaitlistKeys.all });
  }, [qc]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Waitlist Emails" value={stats?.total ?? 0} icon={Mail} accent="bg-brand-green" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setVisualPage(1); }}
            placeholder="Search by email…"
            className="w-full pl-9 pr-8 py-2 text-sm bg-brand-card border border-brand-border rounded-lg text-white placeholder-brand-muted focus:outline-none focus:border-brand-green transition-colors"
          />
          {search && (
            <button onClick={() => { setSearch(""); setVisualPage(1); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted hover:text-white">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <button onClick={handleRefresh} disabled={isFetching} className="btn-ghost text-sm flex items-center gap-1.5">
            {isFetching ? "Refreshing…" : "Refresh"}
          </button>
          <button onClick={() => exportCsv(allRows)} disabled={allRows.length === 0} className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50">
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <div className="card p-8 text-center text-red-400">Failed to load waitlist. Check your API key or network.</div>
      ) : pageRows.length === 0 ? (
        <div className="card p-10 text-center text-brand-muted">No waitlist emails yet.</div>
      ) : (
        <>
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-brand-border">
                    <th className="px-4 py-3 text-left text-brand-muted font-medium">#</th>
                    <th className="px-4 py-3 text-left text-brand-muted font-medium">Email</th>
                    <th className="px-4 py-3 text-left text-brand-muted font-medium">Source</th>
                    <th className="px-4 py-3 text-left text-brand-muted font-medium">Joined</th>
                    <th className="px-4 py-3 text-left text-brand-muted font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r, idx) => (
                    <tr key={r.id} className="border-b border-brand-border/50 hover:bg-brand-border/20 transition-colors">
                      <td className="px-4 py-3 text-brand-muted">{(visualPage - 1) * LIMIT + idx + 1}</td>
                      <td className="px-4 py-3 text-white font-medium">{r.email}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-full text-xs bg-brand-border text-brand-muted">{r.source ?? "—"}</span>
                      </td>
                      <td className="px-4 py-3 text-brand-muted whitespace-nowrap">{formatDate(r.createdAt)}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => deleteEntry(r.id)} disabled={isDeleting} title="Remove"
                          className="p-1.5 rounded-lg text-brand-muted hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-40">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <Pagination
            page={tableMeta.page}
            totalPages={tableMeta.totalPages}
            total={tableMeta.total}
            limit={LIMIT}
            onPageChange={setVisualPage}
            isFetching={isFetching}
          />
        </>
      )}
    </div>
  );
}
